import { lazy, Suspense } from "react";
import { BrowserRouter } from "react-router-dom";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { CookieConsentProvider } from "@/contexts/CookieConsentContext";
import { ComparisonProvider } from "@/contexts/ComparisonContext";
import { AppQueryProvider } from "@/components/AppQueryProvider";
import { ScrollToTop } from "@/components/ScrollToTop";
import { PageSkeleton } from "@/components/ui/PageSkeleton";
import { DeferredAppMount } from "@/components/DeferredAppMount";
import { usePageTracking } from "@/hooks/usePageTracking";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { AppRoutes } from "@/components/AppRoutes";

const CookieConsent = lazy(() =>
  import("@/components/CookieConsent").then((mod) => ({ default: mod.CookieConsent }))
);
const AppToaster = lazy(() =>
  import("@/components/ui/toaster").then((mod) => ({ default: mod.Toaster }))
);
const SonnerToaster = lazy(() =>
  import("@/components/ui/sonner").then((mod) => ({ default: mod.Toaster }))
);

const PageTracker = () => {
  usePageTracking();
  return null;
};

const RoutedApp = () => (
  <>
    <ScrollToTop />
    <PageTracker />
    <ErrorBoundary fallback={<PageSkeleton />}>
      <Suspense fallback={<div className="min-h-screen bg-surface" aria-hidden="true" />}>
        <AppRoutes />
      </Suspense>
    </ErrorBoundary>
    <DeferredAppMount>
      <Suspense fallback={null}>
        <AppToaster />
        <SonnerToaster />
        <CookieConsent />
      </Suspense>
    </DeferredAppMount>
  </>
);

/**
 * Single, stable provider shell. The route tree is always mounted, so navigating
 * between the homepage and any sub-page no longer remounts providers (which caused
 * the white Suspense flash) and never leaves a page without the QueryClient.
 */
const App = () => (
  <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
    <LanguageProvider>
      <CookieConsentProvider>
        <AppQueryProvider>
          <ComparisonProvider>
            <RoutedApp />
          </ComparisonProvider>
        </AppQueryProvider>
      </CookieConsentProvider>
    </LanguageProvider>
  </BrowserRouter>
);

export default App;
